/**
 * Scoped access: roles apply to the whole business unless narrowed to specific websites or domain
 * folders. Identity decides and enforces all of it; these helpers only shape what the UI offers and
 * shows. Pure functions, so they're easy to test.
 */
import { ApiError } from "./http";
import type {
  AccessChange,
  PermissionInfo,
  ResourceGrant,
  ResourceType,
  RestrictionsByRole,
  Role,
  RoleRestrictions,
} from "./types";

export const RESOURCE_TYPES: readonly ResourceType[] = ["site", "domain_folder"];

/**
 * Which permissions can be narrowed, and to what — used until (or if) Identity's catalogue
 * (`GET /v1/permissions`) can't be loaded.
 */
export const NARROWABLE: Readonly<Record<string, ResourceType>> = {
  "websites:read": "site",
  "websites:update": "site",
  "websites:publish": "site",
  "websites:delete": "site",
  "deployments:read": "site",
  "deployments:manage": "site",
  "analytics:read": "site",
  "domains:read": "domain_folder",
  "domains:manage": "domain_folder",
  "dns:manage": "domain_folder",
  "email:read": "domain_folder",
  "email:manage": "domain_folder",
};

/** Permission key → the resource type it can be narrowed to, from Identity's catalogue. */
export function narrowableMap(
  catalogue: PermissionInfo[] | undefined,
): Readonly<Record<string, ResourceType>> {
  if (!catalogue) return NARROWABLE;
  const out: Record<string, ResourceType> = {};
  for (const p of catalogue) if (p.resource_type) out[p.key] = p.resource_type;
  return out;
}

/**
 * The resource types `role` can be narrowed to: its main area, i.e. the types of the permissions it
 * changes, or for a role that only reads, every type it reads. A Domain Manager reads websites only to
 * connect domains to them, so a website limit would leave its domains open (PHP-3833; Identity refuses
 * it with RESTRICTION_OUTSIDE_ROLE_AREA).
 */
export function narrowableTypes(
  role: Pick<Role, "permissions">,
  map: Readonly<Record<string, ResourceType>> = NARROWABLE,
): ResourceType[] {
  // "*" is the Owner: full control, never narrowed.
  if (role.permissions.includes("*")) return [];
  const typed = role.permissions.filter((p) => map[p]);
  const changes = typed.filter((p) => !p.endsWith(":read"));
  const main = changes.length ? changes : typed;
  return RESOURCE_TYPES.filter((t) => main.some((p) => map[p] === t));
}

// ── checking what the signed-in person may do ──────────────────────────────

/**
 * What someone needs to see each area of the portal (sidebar item and its pages). Held on any
 * website or domain folder is enough: a Domain Manager for one folder still gets Domains. Areas not
 * listed (Home, Notifications, Settings, Policies) are for everyone in the business.
 */
export const SECTION_PERMISSIONS: Readonly<Record<string, string>> = {
  "/growth-intelligence": "growth:read",
  "/websites": "websites:read",
  "/domains": "domains:read",
  "/email": "email:read",
  "/hosting": "websites:read",
  "/analytics": "analytics:read",
  "/billing": "billing:read",
  "/team": "members:read",
  "/audit": "audit:read",
  "/activity": "audit:read",
};

export interface PermissionChecks {
  /** Held across the whole business. */
  can: (permission: string) => boolean;
  /** Held across the whole business or on at least one website or domain folder. */
  canAny: (permission: string) => boolean;
  /** Held across the whole business or on this website / domain folder. */
  canOn: (permission: string, type: ResourceType, id: string | null | undefined) => boolean;
}

const grantHolds = (g: ResourceGrant, p: string) => g.permissions.includes("*") || g.permissions.includes(p);

export function permissionChecks(
  orgWide: ReadonlySet<string>,
  grants: readonly ResourceGrant[] = [],
): PermissionChecks {
  const can = (p: string) => orgWide.has("*") || orgWide.has(p);
  return {
    can,
    canAny: (p) => can(p) || grants.some((g) => grantHolds(g, p)),
    canOn: (p, type, id) =>
      can(p) || (!!id && grants.some((g) => g.type === type && g.id === id && grantHolds(g, p))),
  };
}

// ── explaining what someone can't do ────────────────────────────────────────

/**
 * The smallest built-in role that holds each permission (Identity's `SYSTEM_ROLES`), so a disabled
 * action can say what to ask for. Static on purpose: reading the business's roles needs `roles:read`,
 * which a custom role may lack. `null` means only the Owner holds it.
 */
export const ROLE_FOR_PERMISSION: Readonly<Record<string, string | null>> = {
  "organization:read": "Read-only Viewer",
  "organization:update": "Business Administrator",
  "organization:delete": null,
  "members:read": "Read-only Viewer",
  "members:invite": "Business Administrator",
  "members:remove": "Business Administrator",
  "members:manage_roles": "Business Administrator",
  "roles:read": "Read-only Viewer",
  "roles:manage": "Business Administrator",
  "audit:read": "Business Administrator",
  "websites:read": "Read-only Viewer",
  "websites:create": "Website Manager",
  "websites:update": "Website Manager",
  "websites:delete": "Business Administrator",
  "websites:publish": "Website Manager",
  "ai:use": "Website Manager",
  "domains:read": "Read-only Viewer",
  "domains:manage": "Domain Manager",
  "dns:manage": "DNS Manager",
  "email:read": "Email Manager",
  "email:manage": "Email Manager",
  "deployments:read": "Read-only Viewer",
  "deployments:manage": "Website Manager",
  "analytics:read": "Analytics Viewer",
  "billing:read": "Billing Manager",
  "billing:manage": "Billing Manager",
  "growth:read": "Growth Intelligence Viewer",
  "growth:use": "Growth Intelligence Viewer",
  "growth:tasks_manage": "Website Manager",
  "growth:drafts_manage": "Website Manager",
  "growth:approve": "Business Administrator",
  "growth:setup": "Business Administrator",
  "growth:sources_manage": "Business Administrator",
};

/**
 * Why someone can't do something that needs `permission`: "Needs the Domain Manager role. Ask an
 * owner." `elsewhere` says they hold it, just not on this website or domain folder (never named, so
 * nothing about resources outside their access leaks). A permission this map doesn't know falls back
 * to Identity's catalogue description when one is given.
 */
export function missingPermissionReason(
  permission: string,
  { elsewhere = false, catalogue }: { elsewhere?: boolean; catalogue?: readonly PermissionInfo[] } = {},
): string {
  if (elsewhere) {
    const what =
      permission.startsWith("websites:") || permission.startsWith("deployments:")
        ? "website"
        : "domain folder";
    return `Your access doesn't cover this ${what}. Ask an owner to add it.`;
  }
  if (permission in ROLE_FOR_PERMISSION) {
    const role = ROLE_FOR_PERMISSION[permission];
    return role ? `Needs the ${role} role. Ask an owner.` : "Only the Owner can do this.";
  }
  const described = catalogue?.find((p) => p.key === permission)?.description;
  return described
    ? `Needs permission to ${described.charAt(0).toLowerCase()}${described.slice(1)}. Ask an owner.`
    : "Your role doesn't allow this. Ask an owner.";
}

// ── editing someone's restrictions ──────────────────────────────────────────

/**
 * What the access editor holds per role id: a type present means "Selected …" with those ids;
 * absent means everything of that type.
 */
export type ScopeDraft = Record<string, RoleRestrictions>;

/** Starting point of the editor from the roles someone holds (or an invitation's restrictions). */
export function draftFrom(roles: { id: string; restrictions?: RoleRestrictions | null }[]): ScopeDraft {
  const out: ScopeDraft = {};
  for (const r of roles) if (r.restrictions && Object.keys(r.restrictions).length) out[r.id] = r.restrictions;
  return out;
}

/**
 * The `restrictions` body for `role_ids`: only roles being granted, only types each role can be
 * narrowed to, ids sorted and de-duplicated so equal choices compare equal.
 */
export function buildRestrictions(
  roleIds: string[],
  draft: ScopeDraft,
  rolesById: ReadonlyMap<string, Pick<Role, "permissions">>,
  map: Readonly<Record<string, ResourceType>> = NARROWABLE,
): RestrictionsByRole {
  const out: RestrictionsByRole = {};
  for (const id of roleIds) {
    const role = rolesById.get(id);
    const chosen = draft[id];
    if (!role || !chosen) continue;
    const r: RoleRestrictions = {};
    for (const t of narrowableTypes(role, map)) {
      const ids = chosen[t];
      if (ids) r[t] = [...new Set(ids)].sort();
    }
    if (Object.keys(r).length) out[id] = r;
  }
  return out;
}

/** A "Selected …" choice with nothing picked: Identity would grant nothing of that type. */
export function emptySelections(restrictions: RestrictionsByRole): boolean {
  return Object.values(restrictions).some((r) => RESOURCE_TYPES.some((t) => r[t]?.length === 0));
}

/** Stable form of a role change, to tell whether anything changed. */
export function accessSignature(roleIds: string[], restrictions: RestrictionsByRole): string {
  const ids = [...roleIds].sort();
  return JSON.stringify([ids, ids.map((id) => restrictions[id] ?? null)]);
}

// ── describing restrictions and changes ─────────────────────────────────────

/** Names of the business's websites and domain folders, by id. */
export type ResourceNames = Record<ResourceType, ReadonlyMap<string, string>>;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "2 websites · Folder: Clients" — a short caption for a narrowed role. Empty when org-wide. */
export function restrictionSummary(
  restrictions: RoleRestrictions | null | undefined,
  names: ResourceNames,
): string {
  if (!restrictions) return "";
  const parts: string[] = [];
  const sites = restrictions.site;
  if (sites) {
    const name = sites.length === 1 ? names.site.get(sites[0]!) : undefined;
    parts.push(name ? `Website: ${name}` : plural(sites.length, "website", "websites"));
  }
  const folders = restrictions.domain_folder;
  if (folders) {
    const name = folders.length === 1 ? names.domain_folder.get(folders[0]!) : undefined;
    parts.push(name ? `Folder: ${name}` : plural(folders.length, "folder", "folders"));
  }
  return parts.join(" · ");
}

export interface ChangeGroup {
  /** "org", or `${type}:${id}`. */
  key: string;
  title: string;
  permissions: string[];
}

const TYPE_TITLE: Record<ResourceType, [string, string]> = {
  site: ["Website", "a website no longer in this business"],
  domain_folder: ["Folder", "a folder no longer in this business"],
};

/**
 * Groups access changes for display: the whole business first, then each website and folder by
 * name. Permission keys are passed through `label`.
 */
export function groupChanges(
  changes: AccessChange[],
  names: ResourceNames,
  label: (permission: string) => string = (p) => p,
): ChangeGroup[] {
  const groups = new Map<string, ChangeGroup>();
  for (const c of changes) {
    const scoped = c.resource_type && c.resource_id;
    const key = scoped ? `${c.resource_type}:${c.resource_id}` : "org";
    let g = groups.get(key);
    if (!g) {
      let title = "Across the whole business";
      if (c.resource_type && c.resource_id) {
        const [kind, missing] = TYPE_TITLE[c.resource_type];
        title = `${kind}: ${names[c.resource_type].get(c.resource_id) ?? missing}`;
      }
      g = { key, title, permissions: [] };
      groups.set(key, g);
    }
    const text = label(c.permission);
    if (!g.permissions.includes(text)) g.permissions.push(text);
  }
  return [...groups.values()].sort(
    (a, b) => Number(b.key === "org") - Number(a.key === "org") || a.title.localeCompare(b.title),
  );
}

/** Plain words for Identity's refusals of a role change. */
export function accessErrorMessage(error: unknown, business: string): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.reason === "PRIVILEGE_ESCALATION")
    return "You can't give access you don't have yourself. Choose a role with less access, or narrow it to websites or folders you manage.";
  if (error.reason === "LAST_OWNER")
    return `${business} must always have at least one Owner, so this Owner role can't be removed. Add another Owner first.`;
  if (error.reason === "RESTRICTION_OUTSIDE_ROLE_AREA")
    return `${error.message.replace(/\.$/, "")}. Limiting it to anything else would leave its main work open across the whole business.`;
  if (error.status === 422)
    return `That combination of roles and websites or folders isn't possible: ${error.message.replace(/\.$/, "")}.`;
  return null;
}
